import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import LabelPreview from '../components/LabelPreview.jsx';
import ConfirmDialog from '../components/ConfirmDialog.jsx';

const MM_TO_PX = 3.7795;

const STATUS_LABEL = { DRAFT: 'Rascunho', PUBLISHED: 'Publicado' };
const statusBadge = {
  DRAFT: 'bg-surface text-on-surface-variant',
  PUBLISHED: 'bg-primary-container text-on-primary-container',
};

const catLabel = (c) => (c || '').toLowerCase();
const categoryName = (categories, code) => categories.find((c) => c.code === code)?.name || catLabel(code);

function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function SmallStat({ label, value, accent = false }) {
  return (
    <div className={`rounded-[18px] border px-3 py-3 ${accent ? 'border-primary/20 bg-primary-container/15' : 'border-surface-container-high bg-surface-container-low'}`}>
      <div className="text-[9px] font-semibold uppercase tracking-[0.18em] text-on-surface-variant">{label}</div>
      <div className="mt-1 text-[15px] font-semibold tracking-[-0.03em] text-on-surface">{value}</div>
    </div>
  );
}

export default function Templates({ user }) {
  const navigate = useNavigate();
  const canManage = user?.role === 'ADMIN' || user?.role === 'DESIGNER';
  const [list, setList] = useState([]);
  const [formats, setFormats] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', category: 'CUSTOM', description: '', labelFormatId: '', printerId: '' });
  const [renameId, setRenameId] = useState('');
  const [renameValue, setRenameValue] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  async function load() {
    try {
      const [templates, labelFormats, categories, printers] = await Promise.all([
        api.listTemplates(filter ? { category: filter } : {}),
        api.listLabelFormats(),
        api.listCategories(),
        api.listPrinters(),
      ]);
      setList(templates);
      setFormats(labelFormats);
      setCategories(categories);
      setPrinters(printers);
      if (selectedId && !templates.some((item) => item.id === selectedId)) {
        setSelectedId(null);
        setDetail(null);
      }
    } catch (e) {
      setError(e.message);
    }
  }

  async function selectTemplate(template) {
    setSelectedId(template.id);
    setDetail(null);
    setDetailError('');
    try {
      const fetched = await api.getTemplate(template.id);
      setDetail(fetched);
    } catch (e) {
      setDetailError(e.code === 'LABEL_PASSWORD_REQUIRED' ? 'Etiqueta protegida por senha no formato vinculado.' : e.message);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  async function create(e) {
    e.preventDefault();
    setError('');
    setStatus('');
    if (!form.labelFormatId) {
      setError('Vincule um formato de etiqueta para criar a etiqueta.');
      return;
    }
    if (!form.printerId) {
      setError('Selecione uma impressora para a etiqueta.');
      return;
    }
    try {
      const trimmedName = form.name.trim();
      const trimmedDescription = form.description.trim();
      const created = await api.createTemplate({
        name: trimmedName || `Nova etiqueta ${new Date().toLocaleString('pt-BR')}`,
        category: form.category,
        description: trimmedDescription || undefined,
        labelFormatId: form.labelFormatId || undefined,
        printerId: form.printerId || undefined,
      });
      setForm({ name: '', category: 'CUSTOM', description: '', labelFormatId: '', printerId: '' });
      setShowCreate(false);
      setStatus('Projeto de etiqueta criado no estúdio.');
      await load();
      navigate(`/templates/${created.id}/editar`);
    } catch (err) {
      setError(err.message);
    }
  }

  function openDesigner(template) {
    if (template.status === 'PUBLISHED') {
      setError('Para editar, a etiqueta precisa estar no modo rascunho. Volte-a para rascunho antes de abrir o designer.');
      return;
    }
    navigate(`/templates/${template.id}/editar`);
  }

  async function toggleStatus(template) {
    setError('');
    setStatus('');
    await api.updateTemplate(template.id, { status: template.status === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED' });
    setStatus(template.status === 'PUBLISHED' ? 'Etiqueta voltou para rascunho.' : 'Etiqueta publicada para impressão.');
    await load();
  }

  async function remove() {
    if (!deleteTarget) return;
    setError('');
    setStatus('');
    try {
      await api.deleteTemplate(deleteTarget.id);
      setStatus('Etiqueta removida.');
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setError(e.message);
      setDeleteTarget(null);
    }
  }

  async function exportTemplate(template) {
    try {
      const detail = await api.getTemplate(template.id);
      downloadJson(
        `${template.name.replace(/\s+/g, '-').toLowerCase()}.etiqueta.json`,
        {
          template: {
            name: detail.name,
            category: detail.category,
            description: detail.description,
            status: detail.status,
            content: detail.content,
          },
          labelFormat: detail.labelFormat || null,
        },
      );
      setStatus(`Arquivo "${template.name}" exportado.`);
    } catch (e) {
      setError(e.message);
    }
  }

  async function saveRename() {
    const template = list.find((item) => item.id === renameId);
    if (!template) return;
    const trimmed = renameValue.trim();
    setRenameId('');
    if (!trimmed || trimmed === template.name) return;
    setError('');
    setStatus('');
    try {
      await api.updateTemplate(template.id, { name: trimmed });
      setStatus('Nome da etiqueta atualizado.');
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  const publishedCount = list.filter((item) => item.status === 'PUBLISHED').length;
  const draftCount = list.filter((item) => item.status === 'DRAFT').length;

  const previewContent = detail?.content;
  const previewScale = previewContent
    ? Math.min(280 / ((previewContent.width || 102) * MM_TO_PX), 420 / ((previewContent.height || 152) * MM_TO_PX))
    : 1;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-sky-700">EtiquetandoApp Studio</div>
          <h1 className="studio-uniform-title mt-2 font-h2 text-h2 tracking-[-0.04em] text-on-surface">Etiquetas criadas</h1>
        </div>
        <div className="grid min-w-[260px] gap-3 sm:grid-cols-3">
          <SmallStat label="Projetos" value={list.length} accent />
          <SmallStat label="Publicadas" value={publishedCount} />
          <SmallStat label="Rascunhos" value={draftCount} />
        </div>
      </div>

      {error && <p className="rounded-2xl border border-error/20 bg-error-container/40 px-4 py-3 text-[11px] text-error">{error}</p>}
      {status && <p className="rounded-2xl border border-primary/10 bg-primary-container/40 px-4 py-3 text-[11px] text-on-primary-container">{status}</p>}

      <div className="overflow-hidden rounded-[30px] border border-surface-container-highest bg-[linear-gradient(180deg,rgba(255,255,255,0.96)_0%,rgba(246,242,237,0.96)_100%)] shadow-[0_22px_60px_rgba(15,23,42,0.10)]">
        <div className="border-b border-surface-container-high bg-surface-container-low px-5 py-3">
          <div className="flex flex-wrap items-center gap-2">
            {canManage && (
              <button
                type="button"
                onClick={() => setShowCreate((current) => !current)}
                className="inline-flex items-center gap-2 rounded-2xl bg-primary px-3 py-2 text-[10px] font-semibold text-on-primary shadow-[0_12px_24px_rgba(249,115,22,0.2)]"
              >
                <span className="material-symbols-outlined text-[14px]">{showCreate ? 'close' : 'add'}</span>
                {showCreate ? 'Cancelar' : 'Nova etiqueta'}
              </button>
            )}
            <Link to="/formatos" className="inline-flex items-center gap-2 rounded-2xl border border-surface-container-high bg-white px-3 py-2 text-[10px] font-semibold text-on-surface">
              <span className="material-symbols-outlined text-[14px]">crop_portrait</span>
              Formatos
            </Link>
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-[14px] text-on-surface-variant">filter_list</span>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="rounded-2xl border border-surface-container-high bg-white py-2 pl-9 pr-3 text-[10px] text-on-surface outline-none"
              >
                <option value="">Todas as categorias</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {canManage && showCreate && (
          <form onSubmit={create} className="border-b border-surface-container-high bg-white/60 px-5 py-4">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_1fr_1fr_auto]">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nome da etiqueta"
                className="w-full rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
              />
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
              <select
                value={form.labelFormatId}
                onChange={(e) => setForm({ ...form, labelFormatId: e.target.value })}
                className="w-full rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
              >
                <option value="">Sem formato vinculado</option>
                {formats.map((format) => (
                  <option key={format.id} value={format.id}>
                    {format.name}
                  </option>
                ))}
              </select>
              <select
                value={form.printerId}
                onChange={(e) => setForm({ ...form, printerId: e.target.value })}
                className="w-full rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
              >
                <option value="">Selecione a impressora…</option>
                {printers.map((printer) => (
                  <option key={printer.id} value={printer.id}>
                    {printer.name} • {printer.dpi} DPI
                  </option>
                ))}
              </select>
              <input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Descrição do uso, lote, setor ou produto"
                className="w-full rounded-2xl border border-surface-container-high bg-surface px-4 py-3 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
              />
              <button
                type="submit"
                disabled={!form.labelFormatId || !form.printerId}
                className="rounded-2xl bg-primary px-5 py-3 text-[11px] font-semibold text-on-primary shadow-[0_12px_24px_rgba(249,115,22,0.2)] disabled:opacity-40 disabled:shadow-none"
              >
                Criar no estúdio
              </button>
            </div>
          </form>
        )}

        {list.length === 0 ? (
          <div className="px-5 py-8 text-center text-[10px] leading-6 text-on-surface-variant">
            Nenhuma etiqueta cadastrada. Crie um novo projeto ou importe um arquivo.
          </div>
        ) : (
          <div className="flex items-stretch">
            <div className="min-w-0 flex-1 overflow-x-auto transition-all">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="border-b border-surface-container-high">
                    <th className="px-5 py-3 text-[7px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Etiqueta</th>
                    <th className="px-5 py-3 text-[7px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Categoria</th>
                    <th className="px-5 py-3 text-[7px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Status</th>
                    <th className="px-5 py-3 text-[7px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Formato</th>
                    <th className="px-5 py-3 text-[7px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Impressora</th>
                    <th className="px-5 py-3 text-[7px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Atualizada</th>
                    <th className="px-5 py-3 text-right text-[7px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((template) => (
                    <tr
                      key={template.id}
                      onClick={(e) => {
                        if (e.target.closest('button, a, input, select, textarea')) return;
                        selectTemplate(template);
                      }}
                      className={`cursor-pointer border-b border-surface-container-low transition hover:bg-surface-container-low ${selectedId === template.id ? 'bg-primary-container/20' : ''}`}
                    >
                      <td className="px-5 py-3">
                        {renameId === template.id ? (
                          <input
                            autoFocus
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onBlur={saveRename}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.currentTarget.blur();
                              if (e.key === 'Escape') setRenameId('');
                            }}
                            className="w-full max-w-[260px] rounded-lg border border-surface-container-high bg-surface px-2 py-1 text-[11px] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                          />
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <Link to={`/templates/${template.id}/editar`} className="text-[11px] font-semibold text-on-surface hover:text-primary">
                              {template.name}
                            </Link>
                            {canManage && (
                              <button
                                type="button"
                                title="Renomear"
                                onClick={() => {
                                  setRenameId(template.id);
                                  setRenameValue(template.name);
                                }}
                                className="grid h-5 w-5 place-items-center rounded-md text-on-surface-variant transition hover:bg-surface-container-low hover:text-on-surface"
                              >
                                <span className="material-symbols-outlined text-[12px]">drive_file_rename_outline</span>
                              </button>
                            )}
                          </div>
                        )}
                        {template.description && (
                          <div className="mt-0.5 line-clamp-1 max-w-[300px] text-[9px] text-on-surface-variant">{template.description}</div>
                        )}
                      </td>
                      <td className="px-5 py-3 text-[10px] capitalize text-on-surface-variant">{categoryName(categories, template.category)}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.16em] ${statusBadge[template.status]}`}>
                          {STATUS_LABEL[template.status]}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-[10px] text-on-surface-variant">
                        {template.labelFormat
                          ? `${template.labelFormat.name} • ${template.labelFormat.widthMm}x${template.labelFormat.heightMm}`
                          : 'Sem formato'}
                      </td>
                      <td className="px-5 py-3 text-[10px] text-on-surface-variant">
                        {template.printer
                          ? `${template.printer.name} • ${template.printer.dpi} DPI`
                          : 'Sem impressora'}
                      </td>
                      <td className="px-5 py-3 text-[10px] text-on-surface-variant">
                        {new Date(template.updatedAt).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            title="Abrir designer"
                            onClick={() => openDesigner(template)}
                            className="grid h-7 w-7 place-items-center rounded-lg border border-surface-container-high bg-white text-on-surface transition hover:bg-primary-container/20"
                          >
                            <span className="material-symbols-outlined text-[13px]">edit_square</span>
                          </button>
                          <button
                            type="button"
                            title="Exportar"
                            onClick={() => exportTemplate(template)}
                            className="grid h-7 w-7 place-items-center rounded-lg border border-surface-container-high bg-white text-on-surface transition hover:bg-surface-container-low"
                          >
                            <span className="material-symbols-outlined text-[13px]">download</span>
                          </button>
                          {template.status === 'PUBLISHED' && (
                            <Link
                              to={`/impressao?template=${template.id}`}
                              title="Ir para impressão"
                              className="grid h-7 w-7 place-items-center rounded-lg border border-surface-container-high bg-white text-on-surface transition hover:bg-primary-container/20"
                            >
                              <span className="material-symbols-outlined text-[13px]">print</span>
                            </Link>
                          )}
                          {canManage && (
                            <button
                              type="button"
                              title={template.status === 'PUBLISHED' ? 'Voltar para rascunho' : 'Publicar'}
                              onClick={() => toggleStatus(template)}
                              className="grid h-7 w-7 place-items-center rounded-lg border border-surface-container-high bg-white text-on-surface transition hover:bg-primary-container/20"
                            >
                              <span className="material-symbols-outlined text-[13px]">{template.status === 'PUBLISHED' ? 'draft_orders' : 'publish'}</span>
                            </button>
                          )}
                          {canManage && (
                            <button
                              type="button"
                              title="Excluir"
                              onClick={() => setDeleteTarget(template)}
                              className="grid h-7 w-7 place-items-center rounded-lg border border-error/20 bg-error-container/40 text-error transition hover:bg-error-container/70"
                            >
                              <span className="material-symbols-outlined text-[13px]">delete</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {selectedId && (
              <aside className="hidden w-[340px] shrink-0 border-l border-surface-container-high bg-surface-container-low/50 p-5 lg:flex lg:flex-col">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-on-surface-variant">Pré-visualização</div>
                    <div className="mt-1 truncate text-[12px] font-semibold text-on-surface">{detail?.name || list.find((item) => item.id === selectedId)?.name}</div>
                  </div>
                  <button
                    type="button"
                    title="Fechar pré-visualização"
                    onClick={() => {
                      setSelectedId(null);
                      setDetail(null);
                    }}
                    className="grid h-7 w-7 shrink-0 place-items-center rounded-lg border border-surface-container-high bg-white text-on-surface-variant transition hover:text-on-surface"
                  >
                    <span className="material-symbols-outlined text-[13px]">close</span>
                  </button>
                </div>
                <div className="mt-4 flex min-h-0 flex-1 items-center justify-center rounded-[20px] border border-surface-container-high bg-white/70 p-4">
                  {detailError ? (
                    <p className="text-center text-[10px] leading-5 text-on-surface-variant">{detailError}</p>
                  ) : previewContent ? (
                    <div
                      className="overflow-hidden rounded-md border border-surface-container-high bg-white shadow-[0_8px_20px_rgba(15,23,42,0.08)]"
                      style={{ width: `${(previewContent.width || 102) * MM_TO_PX * previewScale}px`, height: `${(previewContent.height || 152) * MM_TO_PX * previewScale}px` }}
                    >
                      <LabelPreview content={previewContent} scale={previewScale} />
                    </div>
                  ) : (
                    <p className="text-center text-[10px] leading-5 text-on-surface-variant">Carregando desenho da etiqueta...</p>
                  )}
                </div>
                <Link
                  to={`/templates/${selectedId}/editar`}
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-3 py-2 text-[10px] font-semibold text-on-primary shadow-[0_12px_24px_rgba(249,115,22,0.2)]"
                >
                  <span className="material-symbols-outlined text-[14px]">edit_square</span>
                  Abrir no estúdio
                </Link>
              </aside>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title={`Remover etiqueta "${deleteTarget?.name}"?`}
        message="Esta ação remove a etiqueta do banco de dados e fica registrada na auditoria de exclusões."
        confirmLabel="Remover"
        onConfirm={remove}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
